import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, Button, ProgressBar, Alert } from 'react-bootstrap';
import axios from 'axios';
import GLOBALVAR from '../../constants';
import './LoanFiles.scss';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const uploadFile = async (file, loanId, updateProgress) => {
    try {
        // Validate file size before upload (5MB limit)
        const maxSize = 5242880; // 5MB in bytes
        if (file.size > maxSize) {
            throw new Error(`File size exceeds 5MB limit`);
        }

        // Step 1: Get presigned URL
        const { data: { uploadUrl, fileKey } } = await axios.post(
            `${GLOBALVAR['SERVER_ROOT']}/api/presigned-upload`,
            {
                fileName: file.name,
                fileType: file.type,
                loanId
            }
        );

        // Step 2: Upload to R2 with minimal headers
        await axios.put(uploadUrl, file, {
            headers: { 
                'Content-Type': file.type
            },
            onUploadProgress: (progressEvent) => {
                const progress = (progressEvent.loaded / progressEvent.total) * 100;
                updateProgress(Math.round(progress));
            }
        });

        // Step 3: Save metadata
        const { data } = await axios.post(
            `${GLOBALVAR['SERVER_ROOT']}/api/loan-files`,
            {
                loanId,
                fileName: file.name,
                fileType: file.type,
                fileKey,
                size: file.size
            }
        );

        return data;
    } catch (error) {
        console.error('Upload failed:', error.response?.data || error.message);
        throw new Error(error.response?.data?.error || 'Upload failed');
    }
};

// Add this helper function at the top of the file, after imports
const getFileUrl = (fileKey) => `${GLOBALVAR['FILES_ROOT']}/${fileKey}`;

const LoanFiles = ({ loanId }) => {
    const [existingFiles, setExistingFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadStatus, setUploadStatus] = useState({
        total: 0,
        current: 0,
        filesCompleted: 0,
        currentFileName: ''
    });
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const fetchExistingFiles = useCallback(async () => {
        try {
            const { data } = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-files/${loanId}`);
            setExistingFiles(data);
        } catch (err) {
            console.error('Fetch error:', err.response?.data || err.message);
            setError('Failed to fetch existing files');
        }
    }, [loanId]);

    useEffect(() => {
        fetchExistingFiles();
    }, [fetchExistingFiles]);

    const onDrop = useCallback(async (acceptedFiles) => {
        setIsUploading(true);
        setError(null);
        setUploadStatus({
            total: acceptedFiles.length,
            current: 0,
            filesCompleted: 0,
            currentFileName: ''
        });

        for (const file of acceptedFiles) {
            try {
                setUploadStatus(prev => ({
                    ...prev,
                    current: 0,
                    currentFileName: file.name
                }));

                await uploadFile(file, loanId, (progress) => {
                    setUploadStatus(prev => ({
                        ...prev,
                        current: progress
                    }));
                });

                setUploadStatus(prev => ({
                    ...prev,
                    filesCompleted: prev.filesCompleted + 1
                }));

                await fetchExistingFiles();
            } catch (err) {
                setError(`Failed to upload ${file.name}: ${err.message}`);
            }
        }

        setIsUploading(false);
        setUploadProgress({});
        setUploadStatus({
            total: 0,
            current: 0,
            filesCompleted: 0,
            currentFileName: ''
        });
    }, [loanId, fetchExistingFiles]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        maxSize: 5242880, // 5MB
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'video/*': ['.mp4', '.mov', '.avi']
        },
        onDropRejected: (rejectedFiles) => {
            const errors = rejectedFiles.map(file => {
                if (file.size > 5242880) {
                    return `${file.file.name} is too large. Maximum size is 5MB`;
                }
                return `${file.file.name} has an unsupported file type`;
            });
            setError(errors.join('\n'));
        }
    });

    const handleDelete = async (fileId) => {
        try {
            await axios.delete(`${GLOBALVAR['SERVER_ROOT']}/api/loan-files/${fileId}`);
            await fetchExistingFiles();
        } catch (err) {
            setError('Failed to delete file');
        }
    };

    const handlePreview = (file, index) => {
        setSelectedFile(file);
        setCurrentIndex(index);
        setViewerOpen(true);
    };

    return (
        <Card className="loan-files-component">
            <Card.Header>
                <h5 className="mb-0">Loan Documents</h5>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'active' : ''} ${isUploading ? 'uploading' : ''}`}
                >
                    <input {...getInputProps()} disabled={isUploading} />
                    <div className="text-center">
                        {isUploading ? (
                            <>
                                <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                                <div className="upload-progress-info">
                                    <p>Uploading files... ({uploadStatus.filesCompleted} of {uploadStatus.total} completed)</p>
                                    <p className="current-file">Current file: {uploadStatus.currentFileName}</p>
                                    <ProgressBar className="mb-2">
                                        <ProgressBar 
                                            variant="success" 
                                            now={(uploadStatus.filesCompleted / uploadStatus.total) * 100} 
                                            label={`${uploadStatus.filesCompleted}/${uploadStatus.total}`} 
                                        />
                                    </ProgressBar>
                                    {uploadStatus.current > 0 && (
                                        <ProgressBar 
                                            now={uploadStatus.current} 
                                            label={`${uploadStatus.current}%`} 
                                            variant="info"
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                                <p>Drag & drop files here, or click to select files</p>
                                <small className="text-muted">
                                    Supported formats: Images, PDF, Documents, Videos
                                </small>
                            </>
                        )}
                    </div>
                </div>

                {Object.keys(uploadProgress).length > 0 && (
                    <div className="mt-3">
                        {Object.entries(uploadProgress).map(([fileName, progress]) => (
                            <div key={fileName} className="mb-2">
                                <small>{fileName}</small>
                                <ProgressBar now={progress} label={`${progress}%`} />
                            </div>
                        ))}
                    </div>
                )}

                <div className="file-list mt-4">
                    {existingFiles.map((file, index) => (
                        <div key={file.id} className="file-item">
                            <div 
                                className="file-preview"
                                onClick={() => handlePreview(file, index)}
                            >
                                {file.fileType.startsWith('image/') ? (
                                    <img 
                                        src={getFileUrl(file.fileKey)} 
                                        alt={file.fileName}
                                        className="preview-thumbnail"
                                    />
                                ) : (
                                    <i className={`fas ${getFileIcon(file.fileType)}`}></i>
                                )}
                            </div>
                            <div className="file-info">
                                <div className="file-name">{file.fileName}</div>
                                <div className="file-meta">
                                    <span>{formatFileSize(file.size)}</span>
                                    <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="file-actions">
                                <Button 
                                    variant="link" 
                                    size="sm"
                                    onClick={() => handlePreview(file, index)}
                                    title="Preview file"
                                >
                                    <i className="fas fa-eye"></i>
                                </Button>
                                {file.url && (
                                    <Button 
                                        variant="link" 
                                        size="sm"
                                        onClick={() => window.open(file.url, '_blank')}
                                        title="Download file"
                                    >
                                        <i className="fas fa-download"></i>
                                    </Button>
                                )}
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    className="text-danger"
                                    onClick={() => handleDelete(file.id)}
                                    title="Delete file"
                                >
                                    <i className="fas fa-trash"></i>
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                {viewerOpen && selectedFile && (
                    <div className="file-viewer-overlay">
                        {selectedFile.fileType.startsWith('image/') ? (
                            <Lightbox
                                open={viewerOpen}
                                close={() => setViewerOpen(false)}
                                slides={existingFiles
                                    .filter(f => f.fileType.startsWith('image/'))
                                    .map(f => ({ src: getFileUrl(f.fileKey) }))}
                                index={currentIndex}
                            />
                        ) : selectedFile.fileType === 'application/pdf' ? (
                            <div className="pdf-viewer">
                                <iframe
                                    src={`${getFileUrl(selectedFile.fileKey)}#toolbar=0`}
                                    title="PDF Viewer"
                                    width="100%"
                                    height="100%"
                                />
                            </div>
                        ) : selectedFile.fileType.startsWith('video/') ? (
                            <div className="video-viewer">
                                <VideoPlayer
                                    src={getFileUrl(selectedFile.fileKey)}
                                    fileType={selectedFile.fileType}
                                />
                            </div>
                        ) : (
                            <div className="unsupported-format">
                                <p>Preview not available for this file type</p>
                                <Button 
                                    variant="primary"
                                    href={getFileUrl(selectedFile.fileKey)} 
                                    target="_blank"
                                >
                                    Download File
                                </Button>
                            </div>
                        )}
                        <Button 
                            className="close-viewer"
                            variant="light"
                            onClick={() => setViewerOpen(false)}
                        >
                            <i className="fas fa-times"></i>
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return 'fa-image';
    if (fileType.startsWith('video/')) return 'fa-video';
    if (fileType === 'application/pdf') return 'fa-file-pdf';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return 'fa-file-excel';
    if (fileType.includes('document') || fileType.includes('word')) return 'fa-file-word';
    return 'fa-file';
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const VideoPlayer = ({ src, fileType }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    useEffect(() => {
        if (!playerRef.current && videoRef.current) {
            const player = videojs(videoRef.current, {
                controls: true,
                fluid: true,
                sources: [{
                    src,
                    type: fileType
                }]
            });
            
            playerRef.current = player;
        }

        return () => {
            if (playerRef.current && playerRef.current.dispose) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [src, fileType]);

    return (
        <div className="video-player-wrapper">
            <div data-vjs-player>
                <video 
                    ref={videoRef}
                    className="video-js vjs-big-play-centered"
                />
            </div>
        </div>
    );
};

export default LoanFiles;